/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Profile reprocessing API", description = "Learn how to programmatically delete and re-ingest profiles in Hightouch Identity Resolution for GDPR compliance and identity graph correction.", lastUpdatedDate = new Date(1789430400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Profile reprocessing API",
        description: "Learn how to programmatically delete and re-ingest profiles in Hightouch Identity Resolution for GDPR compliance and identity graph correction.",
        lastUpdatedDate: "Tue Sep 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Use cases",
          "depth": 0,
          "id": "use-cases"
        }, {
          "value": "How it works",
          "depth": 0,
          "id": "how-it-works"
        }, {
          "value": "API reference",
          "depth": 0,
          "id": "api-reference"
        }, {
          "value": "Workflow",
          "depth": 0,
          "id": "workflow"
        }, {
          "value": "GDPR deletion workflow",
          "depth": 1,
          "id": "gdpr-deletion-workflow"
        }, {
          "value": "Processing timeline",
          "depth": 0,
          "id": "processing-timeline"
        }, {
          "value": "Limitations",
          "depth": 0,
          "id": "limitations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      hr: "hr",
      ul: "ul",
      li: "li",
      ol: "ol",
      code: "code",
      a: "a",
      h3: "h3"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Identity resolution",
        extraText: ". You can use it with or without Customer Studio."
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "profile reprocessing API"
        }), " allows you to programmatically delete and re-ingest identity records for specific profiles. This enables you to handle GDPR deletion requests and correct misidentified profiles by triggering selective reprocessing of your identity graph."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GDPR compliance"
          }), ": Process data deletion requests by removing specific identifiers from your identity graph."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Supercluster resolution"
          }), ": Reprocess profiles after identifying and blocklisting problematic identifiers that caused incorrect merging."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "When you queue profiles for reprocessing, Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Finds all profiles (", _jsx(_components.code, {
            children: "ht_ids"
          }), ") containing the specified identifiers."]
        }), "\n", _jsx(_components.li, {
          children: "Deletes all records associated with those profiles from the identity graph."
        }), "\n", _jsxs(_components.li, {
          children: ["Blocks the given identifiers to prevent them from being re-ingested during future IDR runs. This is the default behavior (", _jsx(_components.code, {
            children: "block: true"
          }), ")."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you're reprocessing to fix a supercluster rather than to delete data, you\ncan set ", _jsx(_components.code, {
            children: "block: false"
          }), " in your API request. This resets the graph state\nwithout permanently blocklisting the identifier, so it can be re-ingested\ncorrectly on the next run."]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "Re-ingests remaining data from your source tables during the next IDR run."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Reprocessing deletes the affected profiles' records and rebuilds them from\nyour source data. A profile that re-forms with the same records keeps its\n", _jsx(_components.code, {
            children: "ht_id"
          }), ". If the records regroup, the ", _jsx(_components.code, {
            children: "ht_id"
          }), " stays with the record that first\ncarried it. Retired ", _jsx(_components.code, {
            children: "ht_id"
          }), " values are never reassigned."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Important:"
          }), " Delete the relevant data from your source tables before calling\nthis API. If the data still exists in your warehouse, it will be re-ingested\nduring the next IDR run."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "api-reference",
        children: "API reference"
      }), "\n", _jsx(_components.p, {
        children: "You can manage profile reprocessing and IDR runs programmatically using the following API endpoints:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/api-reference#operation/QueueForReprocessing",
            children: "Queue for reprocessing"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/api-reference#operation/ReprocessStatus",
            children: "Check reprocessing status"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/api-reference#operation/ListIdrRuns",
            children: "List IDR runs"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/api-reference#operation/TriggerIdrRun",
            children: "Trigger IDR run"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "These endpoints allow you to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Queue identifiers for reprocessing or deletion."
        }), "\n", _jsx(_components.li, {
          children: "Check the status of ongoing or completed jobs."
        }), "\n", _jsx(_components.li, {
          children: "List past and in-progress runs for an identity graph."
        }), "\n", _jsx(_components.li, {
          children: "Manually trigger a new IDR run."
        }), "\n", _jsx(_components.li, {
          children: "Integrate reprocessing logic into your GDPR or identity maintenance pipelines."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For full request and response schemas, authentication requirements, and\nexample code snippets, visit the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/api-reference",
            children: "Hightouch API\nReference"
          }), "."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "workflow",
        children: "Workflow"
      }), "\n", _jsx(_components.h3, {
        id: "gdpr-deletion-workflow",
        children: "GDPR deletion workflow"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete source data"
          }), " — Remove relevant identifiers from your data warehouse tables that feed into IDR."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Queue for reprocessing"
          }), " — Submit a ", _jsx(_components.code, {
            children: "POST"
          }), " request to ", _jsx(_components.code, {
            children: "/idr/{graphId}/queue-for-reprocessing"
          }), " with the identifiers you want to remove."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track the request"
          }), " — The API immediately returns a ", _jsx(_components.code, {
            children: "requestId"
          }), " for asynchronous processing. Check status with ", _jsx(_components.code, {
            children: "GET /idr/{graphId}/reprocess-status/{requestId}"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Automatic reprocessing"
          }), " — During the next scheduled IDR run after the job completes, Hightouch deletes and re-ingests the affected profiles."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "processing-timeline",
        children: "Processing timeline"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The queuing and marking of identifiers happen ", _jsx(_components.strong, {
            children: "asynchronously"
          }), " after you call the API."]
        }), "\n", _jsxs(_components.li, {
          children: ["Actual deletion and re-ingestion occur during the ", _jsx(_components.strong, {
            children: "next IDR run"
          }), " after the job finishes."]
        }), "\n", _jsx(_components.li, {
          children: "Expect one full IDR run cycle to see changes reflected in your resolved identity tables."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Batch size"
          }), ": For large reprocessing requests, split identifiers into multiple batches to avoid timeouts or processing delays."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
